import axios from 'axios';
import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { Link } from 'react-router-dom';
import { Setting, Subscription } from '../../App';
import { CUSTOMER_API_URL } from '../../constants';
import { CategoryProduct, useCategoryProducts } from '../../hooks/use-category-products';
import { SpinButton } from '../SpinButton';
import { CategoryAccordion } from './CategoryAccordion';

import MicroModal from 'micromodal';
import base from '../../styles/Base.module.css';
import style from '../../styles/SubscriptionDetail.module.css';

type TargetProduct = {
  index: number;
  productId: string;
  quantity: number;
  price: number;
};

// sellingPlanAllocationsから定期便価格を取得する。見つからなければnullを返す。
const getSubscriptionPrice = (product: CategoryProduct, sellingPlanId: string): number | null => {
  const firstVariant = product.variants[0];
  if (!firstVariant?.sellingPlanAllocations?.length) return null;

  const allocation = firstVariant.sellingPlanAllocations.find((a) => a.sellingPlan.id === sellingPlanId);
  if (!allocation?.priceAdjustments?.[0]) return null;

  return Math.round(allocation.priceAdjustments[0].price.amount);
};

export const AddProducts: FC<{
  subscription: Subscription;
  setting: Setting;
  planGroups?: any[];
}> = ({ subscription, setting, planGroups = [] }) => {
  const [targetProduct, setTargetProduct] = useState<TargetProduct[]>([]);
  const [targetProductDetail, setTargetProductDetail] = useState<any>(null);
  const [productDetailModalDisplay, setProductDetailModalDisplay] = useState(false);
  const [modalMainImage, setModalMainImage] = useState<string>('');
  const [scrollPosition, setScrollPosition] = useState('');
  const [heavyLoading, setHeavyLoading] = useState(false);

  // アコーディオン用state
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());

  // カテゴリ別商品取得フック
  const { categories, getCategoryProducts, isCategoryLoading, getCategoryProductCount, categoryCache } = useCategoryProducts(
    setting,
    subscription,
  );

  const { deliveryPriceCurrencyCode } = subscription;

  const failureFlagRef = useRef(false);
  const hasExecutedRef = useRef(false);
  const addRecommendProductRef = useRef<((e: MessageEvent) => void) | null>(null);

  // 初回マウント時のみ親にフッター表示を依頼
  useEffect(() => {
    window.parent.postMessage({ action: 'renderSubcription' }, '*');
  }, []);

  // 定期登録済みの金額と数量をフッターに表示するために送る
  useEffect(() => {
    // 登録済み商品は割引後のライン合計を使用
    const productDiscountedTotal = subscription.subscriptionLines.reduce((totalPrice, line) => {
      return totalPrice + line.lineDiscountedPriceAmount;
    }, 0);

    const toBeAddedTotalPrice = targetProduct.reduce((total, product) => {
      if (!product.price) return total;
      const price = product.price * product.quantity;
      return total + price;
    }, 0);

    const combinedSubtotal = productDiscountedTotal + toBeAddedTotalPrice;

    window.parent.postMessage(
      {
        action: 'setTotal',
        totalPrice: combinedSubtotal.toLocaleString(),
        targetProduct: targetProduct,
      },
      '*',
    );

    const handler = (e: MessageEvent) => {
      addRecommendProductRef.current?.(e);
    };
    if (targetProduct.length > 0) {
      window.addEventListener('message', handler);
    }
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [targetProduct, subscription]);

  // refで最新の状態を保持（イベントハンドラ内で使用）
  const openCategoriesRef = useRef(openCategories);
  openCategoriesRef.current = openCategories;

  const getCategoryProductsRef = useRef(getCategoryProducts);
  getCategoryProductsRef.current = getCategoryProducts;

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.targetId) {
        // #を除去してIDを取得
        const targetId = event.data.targetId.replace(/^#/, '');

        // カテゴリを探す
        const category = categories.find((c) => c.anker_id === targetId);
        if (category) {
          // アコーディオンが閉じていたら開く
          if (!openCategoriesRef.current.has(targetId)) {
            setOpenCategories((prev) => {
              const next = new Set(prev);
              next.add(targetId);
              return next;
            });
            // カテゴリの商品を取得
            await getCategoryProductsRef.current(category.title, targetId);
          }
        }
        // スクロール
        scrollToElement(`#${targetId}`);
      }
    };
    const handleScroll = () => {
      // 現在のスクロール位置にある要素の ID を取得
      const currentElementId = getCurrentElementId();
      setScrollPosition(currentElementId);

      // 親ウィンドウに ID を送信
      window.parent.postMessage(
        {
          action: 'setScrollPosition',
          targetId: currentElementId,
        },
        '*',
      );
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [categories]);

  // アコーディオン開閉時にリサイズ
  useLayoutEffect(() => {
    // DOMの更新が完了してから高さを計算
    requestAnimationFrame(() => {
      const height = document.querySelector('body')!.scrollHeight + 100;
      setting.resize(height);
      setting.monitorScroll();
    });
  }, [subscription, openCategories, categoryCache]);

  // アコーディオンのトグル処理
  const handleAccordionToggle = async (categoryId: string, categoryTitle: string) => {
    const isCurrentlyOpen = openCategories.has(categoryId);

    if (isCurrentlyOpen) {
      // 閉じる
      setOpenCategories((prev) => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    } else {
      // 開く - まず開いた状態にしてからデータ取得
      setOpenCategories((prev) => {
        const next = new Set(prev);
        next.add(categoryId);
        return next;
      });
      // カテゴリの商品を取得
      await getCategoryProducts(categoryTitle, categoryId);
    }
  };

  const scrollToElement = (targetId: string) => {
    // 要素が見つかるまでの最大試行回数と間隔を設定
    const maxAttempts = 10;
    const interval = 500; // ミリ秒

    let attempts = 0;
    const intervalId = setInterval(() => {
      const element = document.querySelector(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        clearInterval(intervalId);
      } else if (attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
      attempts++;
    }, interval);
  };

  const getCurrentElementId = () => {
    let closestElementId = '';
    let closestDistanceToCenter = Number.MAX_VALUE;
    const viewportCenter = window.innerHeight / 2;

    document.querySelectorAll('[id]').forEach((element) => {
      const rect = element.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2;
      const distanceToCenter = Math.abs(viewportCenter - elementCenter);

      if (distanceToCenter < closestDistanceToCenter) {
        closestDistanceToCenter = distanceToCenter;
        closestElementId = element.id;
      }
    });

    return closestElementId;
  };

  // 商品追加
  const addRecommendProduct = useCallback(
    async (e: MessageEvent<any>) => {
      // 変更を保存ボタンが押されたとき
      if (e.data.action && e.data.action === 'addSubmit') {
        const planId = subscription.subscriptionLines[0].sellingPlanId;
        let sellingPlanName = '';

        planGroups.forEach((planGroup: any) => {
          const foundPlan = planGroup.plans.find((plan: any) => plan.planId === planId);
          if (foundPlan) {
            sellingPlanName = foundPlan.name;
          }
        });

        if (e.data.targetProduct && e.data.targetProduct.length > 0) {
          if (!hasExecutedRef.current && !heavyLoading) {
            hasExecutedRef.current = true;
            setHeavyLoading(true);

            for (let i = 0; i < e.data.targetProduct.length; i++) {
              const data = e.data.targetProduct[i];

              if (data.quantity === 0) {
                continue;
              }

              // sellingPlanNameをループ内でコピー
              const planName = sellingPlanName;

              setTimeout(async () => {
                const variantId = data.variants[0].id
                  ? `gid://shopify/ProductVariant/${data.variants[0].id}`
                  : data.variants[0].variantId;
                const variantPrice = data.variants[0].price ? data.variants[0].price : data.price;
                const variantSku = data.sku;
                const title = data.variants[0].title ? data.variants[0].title : data.title;

                try {
                  const response = await axios.post(
                    `${CUSTOMER_API_URL}?action=addSubscriptionLine`,
                    {
                      subscriptionId: subscription.subscriptionContractId,
                      planId: planId,
                      planName: planName,
                      subscriptionLine: {
                        productId: data.productId,
                        variantId: variantId,
                        title: data.title,
                        quantity: Number(data.quantity),
                        currentPriceAmount: variantPrice,
                        lineId: null,
                        onlineStorePreviewUrl: data.onlineStorePreviewUrl,
                        variantImage: null,
                        variantTitle: title,
                        currentPriceCurrencyCode: 'JPY',
                        lineDiscountedPriceAmount: variantPrice,
                        lineDiscountedPriceCurrencyCode: 'JPY',
                        sku: variantSku,
                      },
                    },
                    {
                      headers: { Authorization: setting.token },
                    },
                  );
                  console.log({ response });
                } catch (error) {
                  console.error(error);
                  failureFlagRef.current = true;
                }
              }, i * 2000);
            }

            // 実行にかかる時間を算出（各配列の要素×2秒）
            const totalDelay = e.data.targetProduct.length * 2000;

            // 実行にかかる時間を待ってメッセージ表示
            setTimeout(() => {
              setHeavyLoading(false);
              // 次回の追加に備えてリセット
              hasExecutedRef.current = false;

              // ページトップへスクロール（iframe内）
              window.scrollTo({ top: 0, behavior: 'smooth' });
              // 親ページにもスクロールを通知
              window.parent.postMessage({ action: 'scrollToTop' }, '*');

              // モーダルが閉じられた時にリロード
              const onModalClose = () => {
                window.parent.postMessage({ action: 'reloadPage' }, '*');
                window.location.href = '/';
              };

              if (failureFlagRef.current) {
                // 失敗したAPIがあった時
                failureFlagRef.current = false;
                MicroModal.show('modal-complete-add-error', { onClose: onModalClose });
              } else {
                // 全部更新成功したとき
                MicroModal.show('modal-complete-add', { onClose: onModalClose });
                // 選択済み商品をクリア
                setTargetProduct([]);
              }
            }, totalDelay);
          }
        }
      }
    },
    [subscription, planGroups],
  );
  addRecommendProductRef.current = addRecommendProduct;

  if (!subscription) {
    return <></>;
  }

  const deliveryPriceCurrency = deliveryPriceCurrencyCode === 'JPY' ? '￥' : deliveryPriceCurrencyCode;

  // 数量変更
  const sellingPlanId = subscription.subscriptionLines[0]?.sellingPlanId || '';

  const onChangeItemQuantity = (pidx: number, product: CategoryProduct, value: number) => {
    const subscriptionPrice = getSubscriptionPrice(product, sellingPlanId);
    const target = {
      index: pidx,
      quantity: value,
      price: subscriptionPrice ?? (product.price || 0),
      productId: product.productId,
      variants: product.variants,
      title: product.title,
      images: product.images,
      onlineStorePreviewUrl: product.onlineStorePreviewUrl,
      sku: product.sku,
    };

    // すでに登録済みの商品なら、数量変更
    if (targetProduct.find((tp) => tp.productId === product.productId)) {
      setTargetProduct((prev) =>
        prev.map((tp) => {
          if (tp.productId === product.productId) {
            return target;
          }
          return tp;
        }),
      );
    } else {
      // 未登録の商品なら、追加
      setTargetProduct((prev) => [...prev, target]);
    }
  };

  const handleImageClick = (product: CategoryProduct) => {
    const target_product = categoryCache[
      Object.keys(categoryCache).find((key) =>
        categoryCache[key]?.find((p: CategoryProduct) => p.productId === product.productId),
      ) || ''
    ]?.find((p: CategoryProduct) => p.productId === product.productId);

    if (!target_product) return;

    setTargetProductDetail({
      ...target_product,
      onlineStorePreviewUrl: product.onlineStorePreviewUrl,
    });

    window.parent.postMessage(
      {
        action: 'setProductDetail',
        product: target_product,
      },
      '*',
    );
  };

  // 定期便の内容を確認するボタン
  // -------------------------------------------
  const handleConfirmSubscription = () => {
    // 親ウィンドウに ID を送信
    window.parent.postMessage(
      {
        action: 'scrollToSubscription',
      },
      '*',
    );
  };

  return (
    <div className={style.productAdd}>
      <div className={`${style.productHeader}`}>
        <h2 className={base.mypageHeading}>
          <span className={base.mypageHeadingEn}>Additional order</span>
          <span className={base.mypageHeadingJa}>追加注文</span>
        </h2>
      </div>
      <div className={`${base.productList} product`}>
        {categories.map((category) => {
          const isOpen = openCategories.has(category.anker_id);
          const cachedProducts = categoryCache[category.anker_id] || [];
          // 定期購買済み商品を除外（subscription更新後にキャッシュと差分が出るため）
          const shownProducts = cachedProducts.filter(
            (product) =>
              !product.variants.every((variant) =>
                subscription.subscriptionLines.some((line) => line.variantId === variant.variantId),
              ),
          );
          const hasBeenLoaded = categoryCache[category.anker_id] !== undefined;
          // アコーディオンが開いているが、まだ読み込んでいない場合もローディング表示
          const isLoading = isCategoryLoading(category.anker_id) || (isOpen && !hasBeenLoaded);
          const productCount = hasBeenLoaded ? shownProducts.length : getCategoryProductCount(category.anker_id);

          return (
            <CategoryAccordion
              key={category.anker_id}
              category={category}
              isOpen={isOpen}
              onToggle={() => handleAccordionToggle(category.anker_id, category.title)}
              isLoading={isLoading}
              productCount={productCount}
            >
              {shownProducts.length > 0 ? (
                shownProducts.map((product: CategoryProduct, pidx: number) => (
                  <article key={pidx} className={`${base.productItem} ${style.productItemAdd}`}>
                    <div className={style.productSummary}>
                      <div className={style.productImageTitleBlock}>
                        {/* 画像 */}
                        <figure className={base.productItemFigure} onClick={() => handleImageClick(product)}>
                          <img
                            className={base.productImage}
                            src={product.images && product.images.length > 0 ? product.images[0] : ''}
                            alt=""
                          />
                        </figure>
                        <div className={style.productDetail}>
                          <p className={style.productTitle}>{product.title}</p>
                          {/* 価格 */}
                          <div className={style.productDetailPrice}>
                            <div className={base.productItemPrice}>
                              <span className={base.productItemPriceSubscription}>
                                <NumericFormat
                                  displayType="text"
                                  value={getSubscriptionPrice(product, sellingPlanId) ?? (product.price || 0)}
                                  thousandSeparator={true}
                                  prefix={deliveryPriceCurrency}
                                />
                              </span>
                            </div>
                          </div>
                          <div className={style.productQuantityBlock}>
                            <div className={style.productQuantity}>
                              <SpinButton
                                min={0}
                                value={targetProduct.find((tp) => tp.productId === product.productId)?.quantity || 0}
                                onChange={(e) => {
                                  onChangeItemQuantity(pidx, product, e);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                ))
              ) : (
                <p className={style.categoryLoading}>このカテゴリには商品がありません</p>
              )}
            </CategoryAccordion>
          );
        })}
      </div>

      {/* 追加完了モーダル
      -------------------------------------------*/}
      <div className="micromodal" id={`modal-complete-add`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-coupon-title"
          >
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={base.ModalTitle}>
                定期便の内容を更新しました
              </h2>
            </header>
            <main id="modal-coupon-content" className={base.ModalHeader}>
              <div className={`${base.ModalFooter} ${base.ModalFooterSmallLink}`} style={{ textAlign: 'center' }}>
                <Link to={`/`} onClick={() => handleConfirmSubscription()}>
                  定期便の内容を確認する
                </Link>
              </div>
            </main>
          </div>
        </div>
      </div>
      {/* エラーモーダル
      -------------------------------------------*/}
      <div className="micromodal" id={`modal-complete-add-error`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-coupon-title"
          >
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={base.ModalTitle}>
                エラーが発生しました。 <br />
                店舗へお問い合わせください。
              </h2>
            </header>
            <main id="modal-coupon-content" className={base.ModalHeader}>
              <div className={`${base.ModalFooter}`}></div>
            </main>
          </div>
        </div>
      </div>

      {/* 商品詳細モーダル */}
      {productDetailModalDisplay && (
        <>
          <div className={base.modalCover} onClick={() => setProductDetailModalDisplay(false)}></div>
          <div className={base.modal}>
            <h2 className={base.modalTitle}>{targetProductDetail && targetProductDetail.title} の詳細</h2>
            <div className={base.modalContent}>
              {/* 画像 */}
              <div className={base.modalContentLeft}>
                <div className={base.modalImageMain}>
                  <img className={base.modalImageMainImage} src={modalMainImage} />
                </div>
                <div className={base.modalImageList}>
                  {targetProductDetail &&
                    targetProductDetail.media.map((m: any, index: number) => (
                      <div
                        key={`preview_image_${index}`}
                        className={`${base.modalImageItem} ${modalMainImage === m.src ? base.modalImageItem__active : ''}`}
                      >
                        <img src={m.preview_image.src} alt="" className={base.modalImageItemImage} />
                      </div>
                    ))}
                </div>
              </div>

              {/* 商品詳細 */}
              <div className={base.modalContentRight}>
                {targetProductDetail && (
                  <div>
                    <div className={base.modalContentTitle}>{targetProductDetail.title}</div>
                    <div className={base.modalContentDesc}>{targetProductDetail.description}</div>
                    <div className={base.modalContentPrice}>
                      <p className={`${base.modalContentPriceLine} ${base.modalContentPriceLine__right}`}>
                        定期定期便価格:
                        <NumericFormat
                          displayType="text"
                          value={
                            targetProductDetail.variants?.[0]?.sellingPlanAllocations?.length
                              ? (getSubscriptionPrice(targetProductDetail as CategoryProduct, sellingPlanId) ??
                                targetProductDetail.price)
                              : targetProductDetail.price
                          }
                          thousandSeparator={true}
                          prefix={deliveryPriceCurrency}
                          className={base.red}
                        />
                        <span className={base.red}>-</span>
                      </p>
                    </div>

                    {targetProductDetail && (
                      <div className={base.modalFooter}>
                        <button className={base.sectionBtn} onClick={() => setProductDetailModalDisplay(false)}>
                          戻る
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
